import {createRef, Component, cloneElement} from "react";
import PropTypes from "prop-types";
import {observer} from "mobx-react";

import classNames from "classnames";
import styles from "./styles/Modal.module.scss";
import {CSSTransition} from "react-transition-group";
import Button from "../utilities/Button";
import FontAwesome from "../utilities/FontAwesome";
import FlatIcon from "../utilities/FlatIcon";
import {StoreContext} from "../../stores/StoreLoader";
import ModalBackdrop from "./_ModalBackdrop";
import {ErrorBoundary} from "../utilities/ErrorBoundary";
import {getQueries} from "../../utils/SchoolBlocksUtilities";
import {reaction} from "mobx";

const transitionClassNames = {
    enter: styles.transitionEnter,
    enterActive: styles.transitionEnterActive,
    enterDone: styles.transitionEnterDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};

@observer
class Modal extends Component {
    static contextType = StoreContext;
    static propTypes = {
        size: PropTypes.oneOf(["sm", "md", "lg", "sb"]).isRequired,
        theme: PropTypes.oneOf(['classic', 'transparent']),
        index: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        className: PropTypes.string,
        dialogClassName: PropTypes.string,
        containerClassName: PropTypes.string,
        closeButtonClassName: PropTypes.string,
        modalClassName: PropTypes.string,
        width: PropTypes.number,
        closeEffect: PropTypes.func,
        transitionClassNames: PropTypes.object,
        closeButtonComponent: PropTypes.node,
    };

    static defaultProps = {
        size: "lg",
        type: 'default',
        theme: "classic",
    };

    constructor(props) {
        super(props);
        this.state = {
            active: true,
            previousFocus: document.activeElement,
            focusableModalElements: null,
            baseZIndex: 1040,
        }
        this.modalRef = createRef();
    }

    closeModal = () => {
        this.props.closeEffect && this.props.closeEffect();
        this.state.previousFocus && this.state.previousFocus.focus();
        this.setState({
                active: false,
            },
            () => {
                setTimeout(this.context.modalStore.removeTop, 500);
            });
    }

    onMouseDown = (event) => {
        const target = event.target;
        if (target === this.refs.modal) {
            event.stopPropagation();
            this.closeModal();
        }
    };

    onKeyDown = (event) => {
        switch (event.key) {
            case 'Esc':
            case 'Escape':
                this.closeModal();
                break;
        }
    }

    focusFirstElement = () => {
        this.modalRef.current.focus();
    }

    sideBarOpen = () => {
        const queries = getQueries(this.context.interfaceStore.currentFullUrl || "");
        if (queries.spview) {
            document.body.classList.remove(styles.modalOpen);
            this.setState({baseZIndex: 900})
        }
        else if (this.context.modalStore.stack.length !== 0){
            document.body.classList.add(styles.modalOpen);
            this.setState({baseZIndex: 1040})
        }
    }

    componentDidMount() {
        if (this.context.interfaceStore.ExecutionEnvironment.canUseDOM) {
            document.body.classList.add(styles.modalOpen);
        }

        document.addEventListener("keydown", this.onKeyDown);
        this.focusFirstElement();
        reaction(() => [this.context.interfaceStore.currentFullUrl], this.sideBarOpen);
    }

    componentWillUnmount() {
        if (this.context.interfaceStore.ExecutionEnvironment.canUseDOM && this.context.modalStore.stack.length === 0) {
            document.body.classList.remove(styles.modalOpen);
        }

        document.removeEventListener("keydown", this.onKeyDown);
    }

    render() {
        const {index, size} = this.props;

        let backdropZIndex = this.state.baseZIndex + index * 10,
            modalZIndex = backdropZIndex + 1;

        const closeClassName = classNames({
            [styles.close]: true,
            [styles.closeTransparent]: this.props.theme === 'transparent',
            [styles.closeAdmin]: this.props.type === 'admin',
            [this.props.closeButtonClassName]: this.props.closeButtonClassName,
        });
        const modalContentClassName = classNames({
            [styles.modalContent]: true,
            [styles.modalContentTransparent]: this.props.theme === 'transparent',
            [this.props.className]: this.props.className,
        });
        const modalClassName = classNames({
            [styles.modal]: true,
            [styles.modalTransparent]: this.props.theme === 'transparent',
            [this.props.modalClassName]: this.props.modalClassName,
        });
        const dialogClassName = classNames({
            [styles.modalDialog]: true,
            [styles.modalDialogTransparent]: this.props.theme === 'transparent',
            [this.props.dialogClassName]: this.props.dialogClassName,
            [styles.modalSm]: size === "sm",
            [styles.modalMd]: size === "md",
            [styles.modalLg]: size === "lg",
            [styles.modalSchoolblocks]: size === "sb",
        });
        const modalContainerClassName = classNames({
            [styles.modalContainer]: true,
            [styles.modalContainerTransparent]: this.props.theme === 'transparent',
            [this.props.containerClassName]: this.props.containerClassName,
        });

        const ButtonComponent = this.props.closeButtonComponent ? cloneElement(this.props.closeButtonComponent, {
            ...this.props.closeButtonComponent.props,
            "aria-label": "Close",
            onClick: this.closeModal,
        }, this.props.closeButtonComponent.props.children) : <Button
            className={closeClassName}
            onClick={this.closeModal}
            id="modalCloseButton"
            aria-label="Close"
        >
            <FontAwesome ariaHidden={true} prefix={'fas'} name={'fa-times'}/>
        </Button>

        return (
            <div>
                <ModalBackdrop
                    active={this.state.active}
                    onMouseDown={this.onMouseDown}
                    opaqueBackground={true}
                    zIndex={backdropZIndex}
                />
                <div
                    ref="modal"
                    className={modalClassName}
                    style={{zIndex: modalZIndex}}
                    onMouseDown={this.onMouseDown}
                    tabIndex="-1"
                    role="dialog"
                    aria-modal="true"
                >
                    {this.props.theme === 'transparent' && <Button
                        className={closeClassName}
                        onClick={this.closeModal}
                        id="modalCloseButton"
                        aria-label="Close"
                    >
                        <FlatIcon name={'flaticon-close'}/>
                    </Button>}
                    <div
                        className={dialogClassName}
                        style={this.props.width ? {maxWidth: this.props.width} : {}}
                        role="document"
                    >
                        <CSSTransition timeout={0} appear={true} in={this.state.active}
                                       classNames={this.props.transitionClassNames || transitionClassNames}>
                            <div tabIndex={0}
                                 ref={this.modalRef}
                                 className={modalContainerClassName}>
                                {this.props.theme !== 'transparent' && ButtonComponent}
                                <div tabIndex={0}
                                     className={modalContentClassName}>
                                    <ErrorBoundary>
                                        {this.props.children}
                                    </ErrorBoundary>
                                </div>
                                <span tabIndex={0} onFocus={this.focusFirstElement}/>
                            </div>
                        </CSSTransition>
                    </div>
                </div>
            </div>
        );
    }
}

export default Modal
